import { AlertTriangle, Loader2, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { TransactionTierSetting } from "@/interfaces/transaction-tier-settings";
import { useDeleteTransactionSetting } from "@/hooks";

interface DeleteTransactionSettingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  setting: TransactionTierSetting | null;
}

const tierLabel = (tier: string) => {
  return tier
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const typeLabel = (type: string) => {
  return type === "TRANSFER" ? "Transfer" : "Withdrawal";
};

export function DeleteTransactionSettingDialog({
  open,
  onOpenChange,
  setting,
}: DeleteTransactionSettingDialogProps) {
  const deleteMutation = useDeleteTransactionSetting();

  const handleDelete = async () => {
    if (!setting) {
      return;
    }

    try {
      await deleteMutation.mutateAsync(setting.id);

      onOpenChange(false);
    } catch {
      // Error toast is handled by the mutation.
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!deleteMutation.isPending) {
          onOpenChange(nextOpen);
        }
      }}
    >
      <DialogContent className="w-[calc(100%-2rem)] max-w-md border-slate-800 bg-slate-950 text-white">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-red-500/20 bg-red-500/10">
              <AlertTriangle className="h-5 w-5 text-red-400" />
            </div>

            <div>
              <DialogTitle className="text-base text-white">
                Delete Transaction Policy
              </DialogTitle>

              <DialogDescription className="mt-1 text-sm leading-5 text-slate-500">
                This action cannot be undone.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {setting && (
          <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-white">
                  {tierLabel(setting.tier)}
                </p>

                <p className="mt-1 text-xs text-slate-500">
                  {typeLabel(setting.type)} policy
                </p>
              </div>

              <div
                className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${
                  setting.enabled
                    ? "bg-emerald-500/10 text-emerald-400"
                    : "bg-slate-800 text-slate-400"
                }`}
              >
                {setting.enabled ? "Active" : "Disabled"}
              </div>
            </div>
          </div>
        )}

        <div className="rounded-xl border border-amber-500/10 bg-amber-500/5 p-3">
          <p className="text-xs leading-5 text-slate-400">
            Deleting this policy removes its configuration from the
            transaction-tier settings. Make sure another valid policy exists for
            the relevant tier and transaction type.
          </p>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button
            type="button"
            variant="outline"
            disabled={deleteMutation.isPending}
            onClick={() => onOpenChange(false)}
            className="w-full border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800 hover:text-white sm:w-auto"
          >
            Cancel
          </Button>

          <Button
            type="button"
            disabled={deleteMutation.isPending || !setting}
            onClick={handleDelete}
            className="w-full bg-red-500 text-white hover:bg-red-400 sm:w-auto"
          >
            {deleteMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Policy
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
